import { Button, Checkbox, Form, Input, Select, Space } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import EmailIcon from "../../../../assets/icons/EmailIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import CardTitle from "../../../../components/common/CardTitle";
import CustomCard from "../../../../components/common/CustomCard";
import InfoItem from "../../../../components/common/InfoItem";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import { useGroupPermissionAssignMutation } from "../../../../features/administrative-setting/administrativeSetting";
import {
  useMakeSuperAdminMutation,
  useRoleStatusQuery,
} from "../../../../features/emp-setting/employee/employeeApi";
import { useGetAllUsersQuery } from "../../../../features/portal/user/userApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import {
  removeCountryCodeFromNumber,
  selectSearchOption,
} from "../../../../utils/utils";
import EmployeeFileUpload from "../../../admin/Employee/_components/EmployeeFileUpload";
import UserProductManage from "./_components/UserProductManage";

export const userPermissionTabList = [
  {
    label: "Single Permission",
    link: `/admin/user-access-permission/user-list/add`,
  },
  {
    label: "Bulk Permission",
    link: `/admin/user-access-permission/user-list/bulk`,
  },
];

const backLink = "/admin/user-access-permission/user-list";

type TList = {
  productId?: number;
  productName?: string;
  groupId?: number;
  groupName?: string;
  isEdit?: boolean;
};

export default function UserAddPermission() {
  const { id } = useParams();
  const [form] = Form.useForm();
  const { user } = useAppSelector((state) => state.auth);
  const [list, setList] = useState<TList[]>([]);
  const navigate = useNavigate();
  const [groupPermissionAssign, { isLoading, isError, error, isSuccess }] =
    useGroupPermissionAssignMutation();
  useError(isError, error, "Group permission assign fail");
  const { handleOpen, handleClose, open } = useModal<
    boolean | any,
    "CONFIRM_SUPER" | "CONFIRM_CREATE"
  >();
  const {
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError,
    data: userData,
  } = useGetAllUsersQuery({
    params: {
      company_id: user?.data?.company_id,
      status: "active",
    },
  });
  useError(isUserError, userError, "User fetch fail");
  const [
    makeSuperAdmin,
    {
      isLoading: isSuperAdminLoading,
      isError: isSuperAdminError,
      error: superAdminError,
      isSuccess: isSuperAdminSuccess,
    },
  ] = useMakeSuperAdminMutation();
  useError(isSuperAdminError, superAdminError, "Make super admin fail");
  const userId = Form.useWatch("userId", form);
  const {
    isError: isRoleError,
    error: roleError,
    isLoading: isRoleLoading,
    data: roleData,
  } = useRoleStatusQuery(String(userId), {
    skip: !userId,
  });
  useError(isRoleError, roleError, "Get role fail");

  const onFinish = () => {
    if (!list.length) {
      toast("error", "You must choose at least one role group.");
      return;
    }

    const newValues = {
      groupPermissionIds: list?.map((el) => el?.groupId),
      userIds: [userId],
    };

    handleOpen({ type: "CONFIRM_CREATE", data: newValues });
  };

  const singleUser = userData?.data?.users?.find((el) => el?.id === userId);

  useEffect(() => {
    if (!!Object.keys(singleUser || {}).length) {
      form.setFieldsValue({
        username: singleUser?.username,
        email: singleUser?.email,
        mobile_number: singleUser?.mobile
          ? removeCountryCodeFromNumber(singleUser?.mobile)
          : undefined,
        // address:singleUser?.add
        logo: singleUser?.photo,
      });
    }
  }, [form, singleUser]);

  useEffect(() => {
    if (isSuperAdminSuccess) {
      toast(
        "success",
        open?.data
          ? "Successfully make this user a super admin"
          : "Successfully remove super admin status from this user"
      );
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdminSuccess]);

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Group permission assigned successfully");
      navigate(backLink);
    }
  }, [isSuccess, navigate]);

  console.log(userData?.data?.users);

  return (
    <SidebarLayout title="Single Permission" tabList={userPermissionTabList}>
      {open?.type === "CONFIRM_SUPER" && (
        <ConfirmModal
          confirmTitle="Confirm"
          questionText={`Are you sure you want to ${
            open?.data
              ? "make this user a super admin"
              : "remove super admin status from this user"
          }?`}
          okText="Confirm"
          handleClose={handleClose}
          open={open?.open}
          okLoading={isSuperAdminLoading}
          handleOk={() => {
            makeSuperAdmin({
              body: { enable: open?.data },
              id: Number(userId),
            });
          }}
        />
      )}

      {open?.type === "CONFIRM_CREATE" && (
        <ConfirmModal
          confirmTitle="Confirm Action"
          questionText={`Are you sure you want to create this user?`}
          okText="Yes, Confirmed"
          handleClose={handleClose}
          open={open?.open}
          okLoading={isLoading}
          handleOk={() => {
            groupPermissionAssign(open?.data);
          }}
        />
      )}

      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <CustomCard className=" p-6 pb-0 rounded-b-none">
          <CardTitle>User Info</CardTitle>

          <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-x-6 pt-4 mb-6">
            <Form.Item
              label="Select User"
              name="userId"
              rules={[{ required: true, message: "Please select user" }]}
            >
              <Select
                showSearch
                placeholder="Select"
                options={userData?.data?.users?.map((el) => ({
                  label: el?.name || el?.username,
                  value: el?.id,
                }))}
                filterOption={selectSearchOption}
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                loading={isUserLoading}
              />
            </Form.Item>
            <div className="lg:block hidden" />
            <div className="md:block hidden" />
            <Form.Item label="User Name" name="username">
              <Input placeholder="Enter User Name" disabled />
            </Form.Item>

            <Form.Item label="Email ID" name="email">
              <Input
                placeholder="Enter email id"
                prefix={<EmailIcon />}
                disabled
              />
            </Form.Item>

            <Form.Item label="Contact No" name="mobile_number">
              <Input prefix={"+88"} size="large" placeholder="01" disabled />
            </Form.Item>

            {/* <Form.Item label="Address" name="address">
              <Input placeholder="Enter  address" disabled />
            </Form.Item> */}

            <Form.Item
              name="logo"
              label="User Photo"
              className="mb-4"
              valuePropName="fileUrl"
            >
              <EmployeeFileUpload
                form={form}
                accept=".png, .jpg, .jpeg"
                name="logo"
                fileSize={5}
                onChange={(url) =>
                  form.setFieldsValue({
                    logo: url,
                  })
                }
                disabled
              />
            </Form.Item>
          </div>

          <CardTitle>User Role Group</CardTitle>
          <InfoItem rootClassName="!py-3" className="space-x-1">
            <Checkbox
              id="supper_admin"
              checked={roleData?.data?.isSuperAdmin}
              disabled={isRoleLoading || !userId}
              onChange={(checked) => {
                handleOpen({
                  type: "CONFIRM_SUPER",
                  data: checked.target.checked,
                });
              }}
            />{" "}
            <label
              htmlFor="supper_admin"
              className={`text-accent select-none ${
                isRoleLoading || !userId
                  ? "cursor-not-allowed"
                  : "cursor-pointer"
              }`}
            >
              Assign as Super Admin
            </label>
          </InfoItem>

          <UserProductManage list={list} setList={setList} />

          <Space className="w-full my-4" size={12}>
            <Link to={backLink}>
              <Button className="normal-dark-button modal-button" size="middle">
                Cancel
              </Button>
            </Link>
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              // loading={id ? updateSingleUserIsLoading : isCreateUserLoading}
            >
              {id ? "Update" : "Create user"}
            </Button>
          </Space>
        </CustomCard>
      </Form>
    </SidebarLayout>
  );
}
